import useNeveForm from 'components/forms/NeveForm';
import strings from 'strings/strings.json';
import { Headline3 } from 'components/Elements';
import useUploadRequest from 'hooks/useUploadRequest';
import useModifyRequest from 'hooks/useModifyRequest';
import React from 'react';
import { CreateFileSource, CreateVideoTaskRequest } from 'models/inputModels/CreateTaskRequest';
import ResourcesList from '../../ResourcesList';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { FileDocument, FileDocumentType, VideoDocument } from 'models/FileDocument';
import { VideoCard } from '../../FileCard';
import { VideoSelectionComponent } from '../../FileSelectionComponent';
import { CreateActivityFormProps } from './shared/CreateActivityFormProps';
import { LearningResourcesFormModal } from '../../LearningResourcesFormModal';
import { Activity, ActivityType, BuildMethod } from 'models/Activity';
import { ActivityDetailsInput, ResourcesInput } from '../shared/SharedActivityFormComponents';
import { ActivityFormCard } from '../shared/ActivityFormCard';
import BaseDetailsForm from '../shared/DetailsForm';
import { useFieldArray } from 'react-hook-form';

export interface VideoTaskInput extends ActivityDetailsInput {
    resources: ResourcesInput[];
}

export default function CreateVideoTaskForm({ returnToModulesList, moduleId }: CreateActivityFormProps): JSX.Element {
    const { refetchCourse, editMode, setCourseState } = React.useContext(BuildCourseContext);

    const [videoFromLocal, setVideoFromLocal] = React.useState<File | null>();
    const [videoFromProvider, setVideoFromProvider] = React.useState<FileDocument>();
    const [linkModalOpen, setLinkModalOpen] = React.useState(false);
    const [error, setError] = React.useState('');

    const addLinkButtonRef = React.useRef<HTMLButtonElement>(null);
    const selectVideoButtonRef = React.useRef<HTMLButtonElement>(null);
    const removeVideoButtonRef = React.useRef<HTMLButtonElement>(null);

    const { upload: uploadVideo, uploading: uploadingVideo } = useUploadRequest();
    const { modifyData: createTask, loading: creatingTask } = useModifyRequest<CreateVideoTaskRequest, boolean>(
        `activities/task/video`,
        'POST',
    );

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
    } = useNeveForm<VideoTaskInput>({
        title: '',
        description: '',
        durationInMins: null,
        resources: [],
    });

    const {
        fields: resourceFields,
        append: appendResource,
        remove: removeResource,
    } = useFieldArray({
        control,
        name: 'resources',
    });

    React.useEffect(() => {
        if (!videoFromLocal || !videoFromProvider) {
            setError('');
        }
    }, [videoFromLocal, videoFromProvider]);

    async function submit(formData: ActivityDetailsInput) {
        setError('');

        let existingVideoId: string | null = null;
        let videoFilePath: string | null = null;
        let videoFileName: string | null = null;
        let videoUploadDate: Date | null = null;

        if (!videoFromLocal && !videoFromProvider) {
            setError('Video is a required field');
            selectVideoButtonRef.current?.focus();
            return;
        }
        if (videoFromProvider) {
            existingVideoId = videoFromProvider.id;
            videoFileName = videoFromProvider.filename;
            videoFilePath = videoFromProvider.s3Filepath;
            videoUploadDate = videoFromProvider.dateUpdated;
        } else if (videoFromLocal) {
            const { errors, value } = await uploadVideo(videoFromLocal);
            if ((errors && errors.length) || !value) {
                setError('Error creating task');
                return;
            }
            videoFileName = value.filename;
            videoFilePath = value.s3Key;
            videoUploadDate = new Date();
        }

        if (!editMode) {
            const request: CreateVideoTaskRequest = {
                title: formData.title,
                description: formData.description,
                durationInMins: formData.durationInMins ?? 0,
                moduleId: moduleId,
                resources: resourceFields,
                video: videoFromProvider
                    ? {
                          type: 'existing',
                          id: videoFromProvider.id,
                          fileName: videoFromProvider.filename,
                          filePath: videoFromProvider.s3Filepath,
                      }
                    : {
                          type: 'new',
                          fileName: videoFileName!,
                          filePath: videoFilePath!,
                      },
            };

            const { errors } = await createTask(request);
            if (errors && errors.length) {
                setError('Error creating task');
                return;
            }

            await refetchCourse();
            returnToModulesList();
            return;
        }

        setCourseState((prev) => {
            if (!prev) return;
            const moduleIndex = prev.modules.findIndex((m) => m.id === moduleId);
            if (moduleIndex === -1) return prev;
            const module = prev.modules[moduleIndex];

            const newActivityOrder =
                module.activities.length > 0 ? module.activities[module.activities.length - 1].order + 1 : 1;

            const videoDocument: VideoDocument = {
                id: existingVideoId ?? '',
                filename: videoFileName!,
                s3Filepath: videoFilePath!,
                dateUpdated: videoUploadDate!,
                documentType: FileDocumentType.VIDEO,
            };
            const newActivity: Activity = {
                id: '',
                moduleId: moduleId,
                type: ActivityType.TASK,
                buildMethod: BuildMethod.VIDEO,
                title: formData.title,
                description: formData.description,
                durationInMins: formData.durationInMins,
                content: '',
                order: newActivityOrder,
                resources: resourceFields,
                videoDocument: videoDocument,
                dateCreated: new Date(),
                questions: [],
                statuses: [],
                meetings: [],
                imageDocuments: [],
                audioDocument: null,
                generateContentStatus: null,
                taskContents: [],
            };

            const updatedModules = [...prev.modules];
            updatedModules[moduleIndex] = {
                ...module,
                activities: [...module.activities, newActivity],
            };
            return { ...prev, modules: updatedModules };
        });

        returnToModulesList();
        return;
    }

    const addResource = (newResource: ResourcesInput) => {
        appendResource(newResource);
        addLinkButtonRef.current?.focus();
    };
    const deleteResource = (index: number) => {
        removeResource(index);
        addLinkButtonRef.current?.focus();
    };
    const closeLinksModal = () => {
        setLinkModalOpen(false);
        addLinkButtonRef.current?.focus();
    };

    const setVideoFromLocalFunc = (newFile: File | null | undefined) => {
        setVideoFromLocal(newFile);
        setVideoFromProvider(undefined);
    };

    const setVideoFromProviderFunc = (video: FileDocument) => {
        setVideoFromLocal(null);
        setVideoFromProvider(video);
    };

    const removeVideo = () => {
        setVideoFromLocal(null);
        setVideoFromProvider(undefined);
        selectVideoButtonRef.current?.focus();
    };

    let videoName: string;
    let videoFilePath: string;
    let videoSource: CreateFileSource;

    if (videoFromLocal) {
        videoSource = 'local';
        videoName = videoFromLocal.name;
        videoFilePath = URL.createObjectURL(videoFromLocal);
    } else if (videoFromProvider) {
        videoSource = 'provider';
        videoName = videoFromProvider.filename;
        videoFilePath = videoFromProvider.s3Filepath;
    } else {
        videoSource = 'provider';
        videoName = strings.fileUpload.browseFiles;
        videoFilePath = '';
    }

    const videoSelected = videoFromLocal || videoFromProvider;

    const DetailsAndLinks = (
        <>
            <BaseDetailsForm register={register} errors={errors} autoFocus />
            <ResourcesList
                resources={resourceFields}
                deleteResource={deleteResource}
                toggleModalOpen={() => setLinkModalOpen(!linkModalOpen)}
                disabled={uploadingVideo || creatingTask}
                addLinkButtonRef={addLinkButtonRef}
            />
        </>
    );

    const Videos = (
        <>
            <Headline3>{strings.taskContentForm.buildMethod.video}</Headline3>
            {videoSelected ? (
                <VideoCard
                    fileName={videoName}
                    filePath={videoFilePath}
                    source={videoSource}
                    disabled={uploadingVideo || creatingTask}
                    removeFile={removeVideo}
                    removeFileButtonRef={removeVideoButtonRef}
                />
            ) : (
                <VideoSelectionComponent
                    setFileFromLocal={setVideoFromLocalFunc}
                    setFileFromProvider={setVideoFromProviderFunc}
                    setFileFromLocalMetaData={() => {}}
                    disableButton={uploadingVideo || creatingTask}
                    selectFileButtonRef={selectVideoButtonRef}
                />
            )}
        </>
    );

    return (
        <>
            <ActivityFormCard
                title={strings.activityDetailsForm.instruction}
                onSubmit={handleSubmit(submit)}
                pageOne={DetailsAndLinks}
                pageTwo={Videos}
                activityType={ActivityType.TASK}
                error={error}
                submitting={uploadingVideo || creatingTask}
            />
            {linkModalOpen && <LearningResourcesFormModal closeModal={closeLinksModal} addResource={addResource} />}
        </>
    );
}
